// Content for /for/landlords — the `estate` org flavour.
// TRACE: see the note at the top of schools.js. Every `capability` names a spec in
// openspec/specs/ that backs the claim.

export default {
    key: 'landlords',
    eyebrow: 'For landlords, estates and property portfolios',
    title: 'Know which properties are costing you, and prove what you fixed',
    subtitle:
        'Per-property energy, cost and carbon across a whole portfolio, with the outliers ranked, so your next intervention goes where it pays.',
    heroChips: [
        'Per-property cost & occupancy',
        'Property league tables',
        'Tenant recharge statements',
        'Bill validation'
    ],

    features: [
        {
            title: 'One page per property',
            detail: 'Pick a property once and manage its meter connection, occupancy schedule and tariff together, instead of visiting three separate admin pages.',
            capability: 'estate-property-hub'
        },
        {
            title: 'Rank the portfolio, not just the total',
            detail: 'A league table of every property for the period, by consumption, cost or out-of-hours share, with each one’s movement against the previous period, so the properties pulling the total up stand out. Record each property’s floor area and occupancy and the same table also ranks by kWh per m² and per occupant, so a small flat and a large house can be compared fairly; a block’s flats can be compared with one another too.',
            capability:
                'location-profile-fields / estate-property-portfolio / portfolio-rankings-ui'
        },
        {
            title: 'Cost per property, not one blended figure',
            detail: 'Tariffs, standing charges and time-of-use bands evaluated per property against half-hourly data, including Economy 7 and Agile.',
            capability:
                'estate-per-location-cost-occupancy / tariff-model / cost-calculation'
        },
        {
            title: 'Check the supplier got it right, line by line',
            detail: 'Each charge on a supplier statement checked against metered consumption and the rate recorded in your contract, with the exact line and the reason it disagrees shown, not just a total that looks off.',
            capability:
                'bill-validation / bill-statement-model / bill-discrepancy-alert'
        },
        {
            title: 'Know when a property’s contract needs renewing',
            detail: 'Record the supplier, rates and notice period for each property, with an alert before the renewal deadline, so nothing quietly lapses onto deemed out-of-contract rates. At renewal, download a tender pack of the consumption for suppliers to quote on.',
            capability:
                'supply-contract-registry / contract-renewal-alerts / renewal-tender-pack'
        },
        {
            title: 'Onboard a portfolio, not one meter at a time',
            detail: 'Bulk property import with per-property provider credentials, plus a guided setup that tells you what is still missing.',
            capability: 'bulk-location-provisioning / guided-setup'
        },
        {
            title: 'Voids worked out from your tenancies',
            detail: 'Record who lets each property and when, and the days between tenancies count as voids automatically (in the portfolio, each property’s out-of-hours figure and the alert for a void property that keeps using energy), so an empty property’s low use is visible in the data instead of looking like a fault. An away period still marks a void inside a tenancy.',
            capability:
                'tenancy-model / sector-neutral-calendar / estate-per-location-cost-occupancy'
        },
        {
            title: 'Recharge tenants from the meter, not a guess',
            detail: 'Monthly recharge statements built from each tenant’s actual consumption and your tariff, with communal meters shared out, the right VAT and an optional admin fee. Each one is a PDF emailed to the tenant, who can also see past statements when they sign in.',
            capability: 'tenant-recharge / report-subscriptions'
        },
        {
            title: 'A change of tenancy in one sitting',
            detail: 'End one tenancy and start the next with closing and opening readings taken from the half-hourly data, typed in, or from a photo of the meter, and the cost split at the changeover. A replaced meter keeps its history.',
            capability: 'change-of-tenancy / tenancy-model'
        },
        {
            title: 'A read-only login for tenants',
            detail: 'Tenants can see their own property’s usage, alerts and cost, and nothing else in your portfolio.',
            capability: 'location-scoped-access / sector-terminology'
        },
        {
            title: 'The details that matter per property',
            detail: 'Floor area, occupancy, bedrooms, property kind and EPC rating with its expiry date, kept with the property, so comparisons are fair and an expiring certificate is on the record.',
            capability:
                'location-profile-fields / estate-property-hub / portfolio-rankings-ui'
        },
        {
            title: 'Know what a communal plant room uses',
            detail: 'Tag meters as plant and HVAC, EV charging or generation, then see each property’s usage broken down by category and set alerts on just one.',
            capability:
                'meter-categories / estate-property-portfolio / alerts-rules'
        }
    ],

    asides: [
        {
            title: 'Evidence for owners and funders',
            body: 'Annual carbon reporting on the same maintained government factor tables the rest of the platform uses, plus budgets with a seasonally-projected year-end position, so a mid-year conversation about overspend is a projection rather than a guess.',
            capability:
                'carbon-report-generation / budget-model / budget-tracking-ui'
        }
    ],

    proofPoints: [
        'Weather-normalised comparison, so a cold quarter does not read as a failed intervention',
        'Alerts on creeping baseload: the classic signature of something left running in an empty property',
        'Half-hourly detail behind every figure, so a disputed bill has evidence behind it'
    ],

    ctaTitle: 'Start with one building or the whole portfolio',
    ctaBody:
        'Connect a single property to see how it works, then bulk-import the rest: the setup flow tracks what is done and what is outstanding.'
}
